import React, { useEffect, useRef, useState, useCallback } from "react";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import axios from "axios";
import { fetchDevicePoi, fetchDeviceShadow, fetchMonitorAbbrev } from "../utility";




// ── Status dot colors ──────────────────────────────────────
const STATUS_COLORS = {
  moving: "#22c55e",
  parking: "#f59e0b",
  idling: "#f97316",
  offline: "#9ca3af",
  other: "#8b5cf6",
};

function getStatusFromRecord(rec) {
  if (rec.st === "0") return "offline";
  if (rec.sp > 0) return "moving";
  if (rec.ac === 1) return "idling";
  return "parking";
}

// ── Custom truck SVG marker factory ───────────────────────
function makeTruckIcon(color = "#082D6B", isSelected = false) {
  const size = isSelected ? 40 : 32;
  const svg = `
    <svg width="${size}" height="${size}" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <circle cx="20" cy="20" r="19" fill="${color}" stroke="#fff" stroke-width="${isSelected ? 3 : 2}"/>
      <g transform="translate(8,9)" fill="white">
        <rect x="0" y="5" width="16" height="9" rx="1.5"/>
        <path d="M16 7h4l3 4v3h-7z"/>
        <circle cx="4" cy="15" r="2"/>
        <circle cx="18" cy="15" r="2"/>
      </g>
    </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: { width: size, height: size },
    anchor: { x: size / 2, y: size / 2 },
  };
}

// ── Signal strength bars ───────────────────────────────────
const SignalBars = ({ signal }) => {
  const level = Math.min(4, Math.floor((signal / 31) * 4));
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
      {[1, 2, 3, 4].map((b) => (
        <div
          key={b}
          style={{
            width: 4,
            height: 4 + b * 3,
            borderRadius: 1,
            background: b <= level ? "#22c55e" : "#d1d5db",
          }}
        />
      ))}
    </div>
  );
};
const DeviceInfoPopup = ({ record, shadow, poi, onClose,  compact,}) => {
  const dd = shadow?.deviceData;
  const isOnline = record.st === "1";

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
       width: 290,
maxHeight: compact ? "400px" : "none",
overflowY: "visible",
background: "#fff",
        borderRadius: "0 0 8px 0",
        boxShadow: "0 4px 20px rgba(0,0,0,0.18)",
        zIndex: 100,
        overflow: "hidden",
        fontFamily: "inherit",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 14px",
          borderBottom: "1px solid #f0f2f7",
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 700, color: "#1a2540" }}>
          {record.dn}
        </span>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#9aa0b0",
            fontSize: 18,
            lineHeight: 1,
            padding: 0,
          }}
        >
          ×
        </button>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          borderBottom: "1px solid #f0f2f7",
          padding: "0 14px",
        }}
      >
        {["Basic Information", "More"].map((tab, i) => (
          <div
            key={tab}
            style={{
              padding: "8px 0",
              marginRight: 20,
              fontSize: 12,
              fontWeight: i === 0 ? 600 : 400,
              color: i === 0 ? "#082D6B" : "#9aa0b0",
              borderBottom: i === 0 ? "2px solid #082D6B" : "none",
              cursor: "pointer",
            }}
          >
            {tab}
          </div>
        ))}
      </div>

      {/* Body */}
      <div style={{ padding: "12px 14px" }}>
        {/* IMEI + Network */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            marginBottom: 12,
          }}
        >
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#1a2540" }}>
              {record.di}
            </div>
            <div style={{ fontSize: 11, color: "#9aa0b0", marginTop: 2 }}>
              IMEI
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {dd && <SignalBars signal={dd.signal || 0} />}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: 11,
                color: isOnline ? "#22c55e" : "#9aa0b0",
              }}
            >
              <div
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: isOnline ? "#22c55e" : "#9aa0b0",
                }}
              />
              {isOnline ? "Online" : "Offline"}
            </div>
            <div style={{ fontSize: 10, color: "#9aa0b0" }}>Network Status</div>
          </div>
        </div>

        {/* Customer */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 0",
            borderBottom: "1px solid #f5f6fa",
            marginBottom: 8,
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: "#eef2ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="#082D6B"
            >
              <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
            </svg>
          </div>
          <span style={{ fontSize: 12, color: "#1a2540", fontWeight: 500 }}>
            Watsoo Express Pvt Ltd
          </span>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#bbb"
            strokeWidth="2"
            style={{ marginLeft: "auto" }}
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </div>

        {/* Address */}
        <div
          style={{
            display: "flex",
            gap: 8,
            padding: "6px 0",
            borderBottom: "1px solid #f5f6fa",
            marginBottom: 10,
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: "#fff3e0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              marginTop: 2,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#f97316">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
            </svg>
          </div>
          <div>
            <div style={{ fontSize: 12, color: "#1a2540", lineHeight: 1.4 }}>
              {poi?.displayName || `${record.lo}, ${record.la}`}
            </div>
            <div style={{ fontSize: 10, color: "#9aa0b0", marginTop: 4 }}>
              {dd?.typeConvert || "unlocalized"}&nbsp;&nbsp;
              {dd?.reportTime || record.rt}
            </div>
          </div>
        </div>

        {/* Stats grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "6px 0",
          }}
        >
          {[
            {
              label: "Coordinate Points",
              value: `${record.lo}/${record.la}`,
            },
            {
              label: "Direction",
              value: dd?.courseStr || "-",
            },
            {
              label: "Speed",
              value: dd?.speedStr || `${record.sp} km/h`,
            },
            {
              label: "Satellite Count",
              value: dd?.num || "-",
            },
            {
              label: "ACC Status",
              value: dd?.accStr || (record.ac === 1 ? "ON" : "OFF"),
            },
            {
              label: "ACC Update Time",
              value: dd?.accUpdateTime || "-",
            },
            {
              label: "Motion Status",
              value: dd?.typeConvert || "-",
            },
            {
              label: "Accumulated Mileage",
              value: dd?.odometerStr || "0.0 km",
            },
          ].map(({ label, value }) => (
            <React.Fragment key={label}>
              <div
                style={{
                  fontSize: 12,
                  color: "#1a2540",
                  fontWeight: 500,
                  padding: "3px 0",
                }}
              >
                {value}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "#9aa0b0",
                  textAlign: "right",
                  padding: "3px 0",
                }}
              >
                {label}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};
const StatusBar = ({ records }) => {
  const counts = records.reduce(
    (acc, r) => {
      const s = getStatusFromRecord(r);
      acc[s] = (acc[s] || 0) + 1;
      acc.all++;
      return acc;
    },
    { all: 0, moving: 0, parking: 0, idling: 0, offline: 0, other: 0 }
  );

  const pills = [
    { key: "all", label: "All", color: "#082D6B", icon: "⊞" },
    { key: "moving", label: "Moving", color: "#22c55e", icon: "▶" },
    { key: "parking", label: "Parki...", color: "#f59e0b", icon: "P" },
    { key: "idling", label: "Idling", color: "#f97316", icon: "⏸" },
    { key: "offline", label: "Offline", color: "#6b7280", icon: "☾" },
    { key: "other", label: "Other", color: "#8b5cf6", icon: "⚑" },
  ];

  return (
    <div
      style={{
        position: "absolute",
        bottom: 12,
        left: "50%",
        transform: "translateX(-50%)",
        display: "flex",
        gap: 6,
        zIndex: 50,
      }}
    >
      {pills.map(({ key, label, color, icon }) => (
        <div
          key={key}
          style={{
            background: key === "all" ? color : "#fff",
            border: `1px solid ${key === "all" ? color : "#e4e7ef"}`,
            borderRadius: 8,
            padding: "5px 10px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            minWidth: 52,
            boxShadow: "0 1px 4px rgba(0,0,0,0.10)",
          }}
        >
          <span
            style={{
              fontSize: 16,
              fontWeight: 800,
              color: key === "all" ? "#fff" : color,
              lineHeight: 1.1,
            }}
          >
            {counts[key] || 0}
          </span>
          <span
            style={{
              fontSize: 9,
              color: key === "all" ? "rgba(255,255,255,0.8)" : "#9aa0b0",
              marginTop: 2,
            }}
          >
            {icon} {label}
          </span>
        </div>
      ))}
    </div>
  );
};
function useSmoothPositions(records, duration = 15000) {
  const [displayed, setDisplayed] = useState({});
  const animState = useRef({});
  const displayedRef = useRef({});
  const rafRef = useRef(null);

  useEffect(() => {
    displayedRef.current = displayed;
  }, [displayed]);

  useEffect(() => {
    const now = performance.now();

    records.forEach((r) => {
      if (r.la == null || r.lo == null) return;
      const id = r.di;
      const to = { lat: Number(r.la), lng: Number(r.lo) };

      const existing = animState.current[id];
      const currentDisplayed = displayedRef.current[id];

      if (!existing && !currentDisplayed) {
        animState.current[id] = { from: to, to, startTime: now };
        return;
      }

      const lastTarget = existing ? existing.to : currentDisplayed;

      if (
        !lastTarget ||
        lastTarget.lat !== to.lat ||
        lastTarget.lng !== to.lng
      ) {
        const from = currentDisplayed || lastTarget || to;
        animState.current[id] = { from, to, startTime: now };
      }
    });
  }, [records]);

  useEffect(() => {
    const tick = () => {
      const now = performance.now();
      const next = {};

      Object.entries(animState.current).forEach(
        ([id, { from, to, startTime }]) => {
          const elapsed = now - startTime;
          const t = Math.min(1, elapsed / duration);
          const eased =
            t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

          next[id] = {
            lat: from.lat + (to.lat - from.lat) * eased,
            lng: from.lng + (to.lng - from.lng) * eased,
          };
        }
      );

      setDisplayed(next);
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [duration]);

  return displayed;
}

const LiveMapPanel = ({ focusDeviceId, onClose,eventsPanelOpen }) => {
  const mapRef = useRef(null);
  const pollRef = useRef(null);

  const [records, setRecords] = useState([]);
  const [activeId, setActiveId] = useState(null);


  // Per-device shadow/poi data, keyed by device id (di).
  const [shadowById, setShadowById] = useState({});
  const [poiById, setPoiById] = useState({});
  const [loadingPopup, setLoadingPopup] = useState(false);
const [focusedDevice, setFocusedDevice] = useState(null);
const lastFocusedRef = useRef(null);
  const userInteractedRef = useRef(false);
  const programmaticZoomRef = useRef(false);


  const displayedPositions = useSmoothPositions(records, 15000);

  const handleMapLoad = (map) => {
    mapRef.current = map;
  };

  // ── Fetch shadow + poi for one device id and store keyed by id ──
  const fetchDeviceDetails = useCallback(async (deviceId, record) => {
    try {
      const lat = record?.la;
      const lon = record?.lo;
      const [shadowRes, poiRes] = await Promise.all([
        fetchDeviceShadow(deviceId),
        lat != null && lon != null
          ? fetchDevicePoi({ lat, lon })
          : Promise.resolve(null),
      ]);
      if (shadowRes?.code === 0) {
        setShadowById((prev) => ({ ...prev, [deviceId]: shadowRes.data }));
      }
      if (poiRes?.code === 0) {
        setPoiById((prev) => ({ ...prev, [deviceId]: poiRes.data }));
      }
    } catch (err) {
      console.error("Device detail fetch error:", deviceId, err);
    }
  }, []);

  // ── Fetch all device positions (abbrev), then refresh shadow+poi
  //    for every currently focused device in the same interval tick. ──
  const fetchPositions = useCallback(async () => {
    try {
      const res = await fetchMonitorAbbrev();
      if (res?.code === 0) {
        const newRecords = res.data.records || [];
        setRecords(newRecords);

    if (focusedDevice) {
  const rec = newRecords.find(
    (r) => r.di === focusedDevice
  );

  if (rec) {
    await fetchDeviceDetails(
      focusedDevice,
      rec
    );
  }
}
      }
    } catch (err) {
      console.error("LiveMapPanel poll error:", err);
    }
}, [focusedDevice, fetchDeviceDetails]);

  // ── Start polling on mount; restart interval if the focused set
  //    changes so the closure inside always has the latest ids ──
  useEffect(() => {
    fetchPositions();
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(fetchPositions, 15000);
    return () => clearInterval(pollRef.current);
  }, [fetchPositions]);

  // ── Fit map to all markers on first load (only when nothing focused) ──
  useEffect(() => {
  if (!mapRef.current || records.length === 0) return;

  if (focusedDevice) return;

  const bounds =
    new window.google.maps.LatLngBounds();

  records.forEach((r) => {
    if (r.la && r.lo) {
      bounds.extend({
        lat: r.la,
        lng: r.lo,
      });
    }
  });

  if (!bounds.isEmpty()) {
    mapRef.current.fitBounds(bounds);
  }
}, [records, focusedDevice]);
useEffect(() => {
  if (!mapRef.current) return;
  if (!focusedDevice) return;


  if (userInteractedRef.current) return;

  const record = records.find(
    (r) => r.di === focusedDevice
  );

  if (!record) return;

  mapRef.current.panTo({
    lat: Number(record.la),
    lng: Number(record.lo),
  });
  programmaticZoomRef.current = true;
  mapRef.current.setZoom(16);


}, [focusedDevice, records]);
const clearFocus = () => {
  setFocusedDevice(null);
  setActiveId(null);
};


const focusDevice = useCallback(
  (deviceId, record) => {
    lastFocusedRef.current = null;
    // Resume auto panTo/setZoom-on-poll for this new focus session.
    userInteractedRef.current = false;

    setActiveId(deviceId);
    setFocusedDevice(deviceId);

    setLoadingPopup(true);

    fetchDeviceDetails(deviceId, record).finally(() =>
      setLoadingPopup(false)
    );
  },
  [fetchDeviceDetails]
);

  useEffect(() => {
    if (!focusDeviceId) return;
    const record = records.find((r) => r.di === focusDeviceId);

    focusDevice(focusDeviceId, record);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusDeviceId]);


  useEffect(() => {
  if (!focusDeviceId) {
    setActiveId(null);
    setFocusedDevice(null);
  }
}, [focusDeviceId]);

  const handleMarkerClick = (record) => {
    focusDevice(record.di, record);
  };

  const defaultCenter = { lat: 22.5, lng: 88.3 };

  const activeRecord = records.find((r) => r.di === activeId) || null;

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      {/* Optional close button for the panel itself, wired to Dashcam */}
      {onClose && (
        <button
          onClick={onClose}
          title="Close map"
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 110,
            width: 28,
            height: 28,
            borderRadius: "50%",
            border: "1px solid #e4e7ef",
            background: "#fff",
            color: "#1a2540",
            cursor: "pointer",
            fontSize: 16,
            lineHeight: 1,
            boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
          }}
        >
          ×
        </button>
      )}

    <GoogleMap
  onLoad={handleMapLoad}
  onDragStart={() => {
    // Manual pan → stop auto-recentering on this focus session.
    userInteractedRef.current = true;
  }}
  onZoomChanged={() => {
    // Our own setZoom(16) call also fires this event. Ignore that one
    // occurrence so it isn't mistaken for a manual zoom.
    if (programmaticZoomRef.current) {
      programmaticZoomRef.current = false;
      return;
    }
    userInteractedRef.current = true;
  }}
  mapContainerStyle={{
    width: "100%",
    height: "100%",
  }}
  options={{
    disableDefaultUI: false,
    zoomControl: true,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false,
  }}
>
        {records.map((record) => {
          if (!record.la || !record.lo) return null;
          const status = getStatusFromRecord(record);
          const color =
            status === "moving"
              ? STATUS_COLORS.moving
              : status === "offline"
              ? STATUS_COLORS.offline
              : status === "idling"
              ? STATUS_COLORS.idling
              : STATUS_COLORS.parking;
          const isSelected = activeId === record.di;

          const position = displayedPositions[record.di] || {
            lat: record.la,
            lng: record.lo,
          };

          return (
            <Marker
              key={record.di}
              position={position}
              icon={makeTruckIcon(color, isSelected)}
              title={record.dn}
              onClick={() => handleMarkerClick(record)}
              label={
                isSelected
                  ? undefined
                  : {
                      text: record.dn,
                      fontSize: "10px",
                      fontWeight: "600",
                      color: "#1a2540",
                    }
              }
            />
          );
        })}
      </GoogleMap>

      {/* Device Info Popup — shows for the active (most recently
          clicked/focused) device, positioned top-left of the panel */}
      {activeRecord && (
        <DeviceInfoPopup
  record={activeRecord}
  shadow={loadingPopup ? null : shadowById[activeId]}
  poi={loadingPopup ? null : poiById[activeId]}
  onClose={() => setActiveId(null)}
  compact={eventsPanelOpen}
/>
      )}

      {/* Status bar bottom */}
      <StatusBar records={records} />
    </div>
  );
};

export default LiveMapPanel;